import { useCallback, useEffect, useRef, useState } from 'react';
import { api, type QuarantineItem } from '../shared/api.ts';
import { formatBytes, formatWhen } from '../shared/format.ts';
import { SwipeCard, type SwipeAction } from './SwipeCard.tsx';
import { Thumb } from './Thumb.tsx';

const UNDO_MS = 5000;

interface PendingAction {
  item: QuarantineItem;
  action: SwipeAction;
  timer: number;
}

export function Quarantine({
  items,
  loading,
  onChanged,
}: {
  items: QuarantineItem[];
  loading: boolean;
  onChanged: () => void;
}): React.JSX.Element {
  const [resolving, setResolving] = useState<PendingAction | null>(null);
  const [error, setError] = useState<string | null>(null);
  const timers = useRef<number[]>([]);

  useEffect(() => {
    const list = timers.current;
    return () => {
      for (const t of list) window.clearTimeout(t);
    };
  }, []);

  const commit = useCallback(
    async (item: QuarantineItem, action: SwipeAction) => {
      try {
        if (action === 'keep') await api.keep(item.id);
        else await api.discard(item.id);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed');
      } finally {
        setResolving((current) => (current?.item.id === item.id ? null : current));
        onChanged();
      }
    },
    [onChanged],
  );

  /**
   * The decision is held locally for five seconds before it reaches the server.
   * That is what makes the undo real: nothing has happened yet, so there is
   * nothing to reverse.
   */
  const resolve = useCallback(
    (item: QuarantineItem, action: SwipeAction) => {
      setError(null);
      const timer = window.setTimeout(() => void commit(item, action), UNDO_MS);
      timers.current.push(timer);
      setResolving((current) => {
        if (current) {
          window.clearTimeout(current.timer);
          void commit(current.item, current.action);
        }
        return { item, action, timer };
      });
    },
    [commit],
  );

  const undo = useCallback(() => {
    setResolving((current) => {
      if (current) window.clearTimeout(current.timer);
      return null;
    });
  }, []);

  const visible = items.filter((item) => item.id !== resolving?.item.id);

  return (
    <div className="pane">
      {error ? <p className="pane__error mono">{error}</p> : null}

      {visible.length === 0 && !loading ? (
        <p className="pane__empty">Nothing waiting.</p>
      ) : (
        <ul className="cards">
          {visible.map((item) => (
            <li key={item.id}>
              <SwipeCard onResolve={(action) => resolve(item, action)}>
                <article className="card">
                  <Thumb id={item.id} mime={item.mime} hasThumbnail={item.hasThumbnail} name={item.name} />

                  <div className="card__body">
                    <h2 className="card__name">
                      {item.relativePath ? (
                        <span className="card__path mono">{item.relativePath}/</span>
                      ) : null}
                      {item.name}
                    </h2>
                    <p className="card__facts mono">
                      {formatBytes(item.size)} · {formatWhen(item.createdAt)}
                    </p>
                    <p className="card__origin mono">
                      {item.origin.ip ?? 'unknown'}
                      {item.origin.city ? ` · ${item.origin.city}` : ''}
                      {item.origin.country ? `, ${item.origin.country}` : ''}
                    </p>
                    {item.scanResult ? (
                      <p className="card__warning mono">malware: {item.scanResult}</p>
                    ) : null}
                  </div>

                  <div className="card__actions">
                    <button
                      type="button"
                      className="button button--quiet"
                      onClick={() => resolve(item, 'discard')}
                    >
                      Discard
                    </button>
                    <button
                      type="button"
                      className="button button--quiet"
                      onClick={() => resolve(item, 'keep')}
                    >
                      Keep
                    </button>
                  </div>
                </article>
              </SwipeCard>
            </li>
          ))}
        </ul>
      )}

      {resolving ? (
        <div className="undo" role="status">
          <span>
            {resolving.action === 'keep' ? 'Kept' : 'Discarded'} {resolving.item.name}
          </span>
          <button type="button" className="undo__button" onClick={undo}>
            Undo
          </button>
        </div>
      ) : null}
    </div>
  );
}
